import React, { useRef, useState } from 'react'
import { Box, Chip, IconButton, Typography } from '@mui/material'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import { ALLOWED_PATIENT_FILE_TYPES, useUploadPatientFile } from '../../../api/useUploadPatientFile'
import { advancedColors } from '../../../theme'

export type PendingAttachment = { fileID: string; filename: string; contentType: string }

// advanced/service's cap, checked here since it only rejects after upload.
const MAX_ATTACHMENTS = 10

export const useAttachmentUpload = (uid: string, disabled?: boolean) => {
  const upload = useUploadPatientFile(uid)
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const addFiles = async (fileList: FileList | null) => {
    if (disabled || !fileList || fileList.length === 0) {
      return
    }
    setError(null)
    const files = Array.from(fileList)
    if (pending.length + files.length > MAX_ATTACHMENTS) {
      setError(`A message can carry at most ${MAX_ATTACHMENTS} files.`)
      return
    }
    setUploading(true)
    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const { fileID, filename } = await upload(file)
          return { fileID, filename, contentType: file.type }
        })
      )
      setPending((current) => [...current, ...uploaded])
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const remove = (fileID: string) => setPending((current) => current.filter((file) => file.fileID !== fileID))
  const clear = () => setPending([])

  return { pending, addFiles, remove, clear, uploading, error }
}

type ButtonProps = {
  disabled?: boolean
  onDark?: boolean
  onSelect: (files: FileList | null) => void
}

export const AttachmentButton = ({ disabled, onSelect, onDark = false }: ButtonProps) => {
  const input = useRef<HTMLInputElement | null>(null)

  return (
    <>
      <input
        ref={input}
        type="file"
        accept={ALLOWED_PATIENT_FILE_TYPES.join(',')}
        multiple
        hidden
        onChange={(event) => {
          onSelect(event.target.files)
          if (input.current) {
            input.current.value = ''
          }
        }}
      />
      <IconButton
        size="small"
        disabled={disabled}
        onClick={() => input.current?.click()}
        aria-label="Attach an image or PDF"
        sx={onDark ? { color: advancedColors.surface, '&.Mui-disabled': { color: advancedColors.line } } : undefined}
      >
        <AttachFileRoundedIcon fontSize="small" />
      </IconButton>
    </>
  )
}

type PreviewProps = {
  pending: PendingAttachment[]
  onRemove: (fileID: string) => void
  uploading: boolean
  error: string | null
}

export const AttachmentPreview = ({ pending, onRemove, uploading, error }: PreviewProps) => {
  if (pending.length === 0 && !uploading && !error) {
    return null
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, px: 1, pt: 1 }}>
      {pending.map((file) => (
        <Chip key={file.fileID} size="small" label={file.filename} onDelete={() => onRemove(file.fileID)} />
      ))}
      {uploading && (
        <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
          Uploading…
        </Typography>
      )}
      {error && (
        <Typography variant="caption" sx={{ color: advancedColors.error }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
